import type { HubColorKey, RelatedLink, PageContext, PageKind } from './entities';

const SECTION_TAG: Record<HubColorKey, string> = {
  markets: 'Markets & News',
  stocks: 'Sectors',
  ratings: 'Ratings',
  trading: 'Trading',
  screeners: 'Screeners',
  money: 'Personal Finance',
  brand: 'Benzinga',
};

export const relatedLinkTag = (link: RelatedLink): string => link.tag ?? SECTION_TAG[link.hub];

type LinkResolver = (ctx: PageContext) => RelatedLink[];

interface SectionRelatedLinks {
  heading: string;
  description?: string;
  links: Partial<Record<PageKind, LinkResolver>> & { generic: LinkResolver };
}

export interface ResolvedRelatedLinks {
  heading: string;
  description?: string;
  entityLabel?: string;
  links: RelatedLink[];
}

const tickerNews = (t: string): RelatedLink => ({ label: `${t} News`, href: `/quote/${t}/news`, hub: 'markets' });
const analystRatings = (t: string): RelatedLink => ({
  label: 'Analyst Ratings',
  href: `/quote/${t}/price-targets`,
  hub: 'ratings',
});
const optionsActivity = (t: string): RelatedLink => ({
  label: 'Options Activity',
  href: `/quote/${t}/options`,
  hub: 'trading',
});
const ownership = (t: string): RelatedLink => ({
  label: 'Ownership & Insiders',
  href: `/quote/${t}/ownership`,
  hub: 'ratings',
});
const chart = (t: string): RelatedLink => ({ label: 'Interactive Chart', href: `/quote/${t}/charts`, hub: 'stocks' });
const comparePeers = (t: string): RelatedLink => ({
  label: 'Compare Peers',
  href: `/quote/${t}/compare-peers`,
  hub: 'screeners',
});
const sectorJump = (ctx: PageContext): RelatedLink | null =>
  ctx.sectorSlug
    ? { label: `${ctx.sector ?? 'Sector'} Stocks`, href: `/topic/${ctx.sectorSlug}`, hub: 'stocks', tag: 'Sectors' }
    : null;

const compactLinks = (...entries: Array<RelatedLink | null>): RelatedLink[] =>
  entries.filter((entry): entry is RelatedLink => entry != null);

const SECTION_RELATED: Record<string, SectionRelatedLinks> = {
  'markets-news': {
    heading: 'Related across Benzinga',
    links: {
      generic: () => [
        { label: 'Top Movers', href: '/movers', hub: 'stocks' },
        { label: "Today's Rating Changes", href: '/analyst-stock-ratings', hub: 'ratings' },
      ],
      quote: ctx =>
        compactLinks(
          tickerNews(ctx.ticker!),
          analystRatings(ctx.ticker!),
          optionsActivity(ctx.ticker!),
          sectorJump(ctx),
        ),
    },
  },

  'stocks-sectors': {
    heading: 'Related across Benzinga',
    links: {
      generic: () => [
        { label: 'Stock Screener', href: '/screener', hub: 'screeners' },
        { label: 'Options Activity', href: '/calendars/unusual-options-activity', hub: 'trading' },
      ],
      quote: ctx =>
        compactLinks(
          tickerNews(ctx.ticker!),
          analystRatings(ctx.ticker!),
          optionsActivity(ctx.ticker!),
          sectorJump(ctx),
        ),
    },
  },

  'ratings-research': {
    heading: 'Related across Benzinga',
    links: {
      generic: () => [
        { label: 'Top-Ranked Screen', href: '/screener/top-value-stocks', hub: 'screeners' },
        { label: 'Ratings Feed', href: '/analyst-stock-ratings/upgrades', hub: 'markets' },
      ],
      quote: ctx => [analystRatings(ctx.ticker!), ownership(ctx.ticker!), tickerNews(ctx.ticker!)],
    },
  },

  trading: {
    heading: 'Related across Benzinga',
    links: {
      generic: () => [
        { label: 'Options Activity', href: '/calendars/unusual-options-activity', hub: 'stocks' },
        { label: 'Try Benzinga Pro', href: '/pro/', hub: 'brand' },
      ],
      quote: ctx => [optionsActivity(ctx.ticker!), chart(ctx.ticker!), tickerNews(ctx.ticker!)],
    },
  },

  'screeners-lists': {
    heading: 'Related across Benzinga',
    links: {
      generic: () => [
        { label: 'Live Market News', href: '/latest', hub: 'markets' },
        { label: 'How to Invest', href: '/money/how-to-invest-money', hub: 'money' },
      ],
      quote: ctx => compactLinks(comparePeers(ctx.ticker!), sectorJump(ctx), tickerNews(ctx.ticker!)),
    },
  },

  'personal-finance': {
    heading: 'Related across Benzinga',
    links: {
      generic: () => [
        { label: 'Top Value Stocks', href: '/screener/top-value-stocks', hub: 'screeners' },
        { label: 'Live Market News', href: '/latest', hub: 'markets' },
      ],
    },
  },
};

export const resolveRelatedLinks = (sectionKey: string, ctx: PageContext): ResolvedRelatedLinks | null => {
  const config = SECTION_RELATED[sectionKey];
  if (!config) return null;

  const entityResolver = ctx.ticker ? config.links[ctx.kind] ?? config.links.quote : undefined;
  const resolver = entityResolver ?? config.links.generic;
  const entityLabel = entityResolver ? ctx.tickerLabel ?? ctx.ticker : undefined;

  return {
    heading: config.heading,
    description: config.description,
    entityLabel,
    links: resolver(ctx),
  };
};

const QUOTE_PATH = /^\/quote\/([^/?#]+)/i;

export const hidesHubSearchAndPremium = (pathname: string | null | undefined): boolean => {
  if (!pathname) return false;
  const path = pathname.split('?')[0]?.split('#')[0] ?? pathname;
  return path === '/money' || path.startsWith('/money/') || path === '/learn' || path.startsWith('/learn/');
};

export const resolvePageContext = (
  pathname: string | null | undefined,
  extra?: { articleTicker?: string; sector?: string; sectorSlug?: string },
): PageContext => {
  const match = pathname ? QUOTE_PATH.exec(pathname) : null;
  if (match) {
    const ticker = decodeURIComponent(match[1]).toUpperCase();
    return { kind: 'quote', ticker, tickerLabel: ticker, sector: extra?.sector, sectorSlug: extra?.sectorSlug };
  }

  if (extra?.articleTicker) {
    const ticker = extra.articleTicker.toUpperCase();
    return { kind: 'article', ticker, tickerLabel: ticker, sector: extra?.sector, sectorSlug: extra?.sectorSlug };
  }

  return { kind: 'generic' };
};
